import type { CSSProperties, Ref } from 'vue';
import { defineComponent, h, reactive, ref } from 'vue';

/**
 * It is important to keep states outside the useTooltip composable function.
 * So that it works as a shared state between multiple components.
 */

const isVisible = ref(false);
const text = ref('');

const commonStyles = reactive<CSSProperties>({
  position: 'absolute',
  pointerEvents: 'none',
});

const tooltipSpecificStyles = reactive<CSSProperties>({
  top: '0px',
  left: '0px',
  zIndex: '1000',
  display: 'flex',
  width: 'max-content',
  maxWidth: '17em',
  height: 'auto',
  padding: '12px 16px',
  transform: 'translate(-50%, -100%)',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--primary-text-color)',
  boxShadow: '0 0 5px rgba(0, 0, 0, 0.3)',
  backgroundPosition: 'center',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  borderRadius: '4px',
  backgroundColor: 'var(--primary-bg-color)',
});

// Styles for the arrow
const arrowSpecificStyles = reactive<CSSProperties>({
  top: '99%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '0',
  height: '0',
  borderLeft: '6px solid transparent',
  borderRight: '6px solid transparent',
  borderTop: '6px solid var(--primary-bg-color)',
});

const tooltipStyles = reactive<CSSProperties>({
  ...commonStyles,
  ...tooltipSpecificStyles,
});

const arrowStyles = reactive<CSSProperties>({
  ...commonStyles,
  ...arrowSpecificStyles,
});

/**
 * Clamps the tooltip X position so the box never overflows the viewport.
 * The tooltip is centred on positionX via translate(-50%), so the visible
 * left edge is (positionX - halfWidth) and the right edge is
 * (positionX + halfWidth). The tooltip max-width is configured in `em`,
 * so we derive the pixel half-width from the current root font size instead
 * of assuming a fixed 16px base font size. We also guard against narrow
 * viewports where maxX < minX would produce an invalid clamp range.
 */
const TOOLTIP_MAX_WIDTH_EM = 17;
const EDGE_MARGIN = 8;

function getRootFontSize(): number {
  const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);

  return Number.isFinite(rootFontSize) ? rootFontSize : 16;
}

function clampToViewport(positionX: number): number {
  const viewportWidth = window.innerWidth;
  const halfMaxWidth = (TOOLTIP_MAX_WIDTH_EM * getRootFontSize()) / 2;
  const availableWidth = Math.max(viewportWidth - EDGE_MARGIN * 2, 0);
  const effectiveHalfWidth = Math.min(halfMaxWidth, availableWidth / 2);
  const minX = effectiveHalfWidth + EDGE_MARGIN;
  const maxX = viewportWidth - effectiveHalfWidth - EDGE_MARGIN;

  return Math.min(Math.max(positionX, minX), maxX);
}

interface UseTooltipReturn {
  text: Ref<string>;
  isVisible: Ref<boolean>;
  tooltipStyles: CSSProperties;
  showTooltip: (content: string, tooltipPositionX: number, tooltipPositionY: number) => void;
  hideTooltip: () => void;
}

/**
 * useTooltip is a composable function that returns a set of methods to show and hide a tooltip.
 * It sets the text content and position of the tooltip based on the arguments passed.
 *
 * @returns {Object} - An object containing methods to show and hide the tooltip.
 */
const useTooltip = (): UseTooltipReturn => {
  const showTooltip = (content: string, tooltipPositionX: number, tooltipPositionY: number) => {
    text.value = content;
    const clampedX = clampToViewport(tooltipPositionX);
    const offset = tooltipPositionX - clampedX;
    tooltipStyles.left = `${clampedX}px`;
    tooltipStyles.top = `${tooltipPositionY}px`;
    arrowStyles.left = `calc(50% + ${offset}px)`;
    isVisible.value = true;
  };

  const hideTooltip = () => {
    isVisible.value = false;
  };

  return {
    text,
    isVisible,
    tooltipStyles,
    showTooltip,
    hideTooltip,
  };
};

/**
 * BaseTooltip is a component constructed using the render function to attach at root level.
 * Creates a tooltip vnode and returns it if the tooltip is set to be visible.
 * Tooltip vnode is just a div with some styles and inner text as child.
 *
 * @component BaseTooltip
 */
const BaseTooltip = defineComponent({
  setup() {
    return () => {
      const arrow = h('div', { style: arrowStyles });
      const tooltip = h('div', { style: tooltipStyles }, [text.value, arrow]);

      return isVisible.value ? tooltip : null;
    };
  },
});

export { BaseTooltip, useTooltip };
